import React, { useState, useMemo, useCallback } from 'react'
import { Link } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import GoLiveBanner from '@/components/onboarding/GoLiveBanner'
import { HelpTooltip } from '@/components/HelpTooltip'
import MonthPicker from '@/components/calendar/MonthPicker'
import RateGrid from '@/components/calendar/RateGrid'
import CalendarMonthGrid from '@/components/calendar/CalendarMonthGrid'
import TimelineGrid from '@/components/calendar/TimelineGrid'
import ViewSwitcher from '@/components/calendar/ViewSwitcher'
import PerformanceBanner from '@/components/calendar/PerformanceBanner'
import CalendarUpdateBanner from '@/components/calendar/CalendarUpdateBanner'
import MultiSelectDropdown from '@/components/calendar/MultiSelectDropdown'
import SingleSelectDropdown from '@/components/calendar/SingleSelectDropdown'
import ComparisonSelector from '@/components/calendar/ComparisonSelector'
import DayDetailModal from '@/components/calendar/DayDetailModal'
import NotPricedCalendarState from '@/components/calendar/NotPricedCalendarState'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { FigureScopeContext } from '@/lib/figure-scope'
import { todayIso } from '@/lib/calendar-utils'
import { buildRoomTypeRowMeta } from '@/lib/room-type-tree'
import { isNotPriced } from '@/lib/pricing-status'
import { useCalendarNav } from '@/hooks/useCalendarNav'
import type { SharedProps } from '@/types/shared'
import type {
  DayInfo, RoomType, RatePlan, RateData, OccupancyDay, AdrDay,
  RateHistoryEntry, PerformanceMetrics, ViewMode, ComparisonMode, RoomOccupancyEntry,
} from '@/types/calendar'

interface TargetMarker {
  state: string
  reasonCopy: string
  targetYear: number
}

interface PricesProps extends SharedProps {
  month: number
  year: number
  /**
   * The building's rooms (server, App::BuildingRooms): at a combined inventory the lead's rooms and the
   * Added rooms, a name two of them share carrying its listing in brackets. A Same room is not a row;
   * how each listing sells a room is on its hover card and in the day details.
   */
  roomTypes: RoomType[]
  ratePlans: RatePlan[]
  selectedRatePlanId: number
  rates: Record<string, RateData>
  allRoomTypeRates: Record<string, RateData>
  daysInMonth: DayInfo[]
  comparisonData: Record<string, number | null>
  comparisonMode: string
  performance: PerformanceMetrics
  occupancyData: OccupancyDay[]
  roomOccupancy?: Record<string, RoomOccupancyEntry>
  totalRooms: number
  /** "Building" at a combined inventory of two or more listings; null keeps the pooled chip's "Hotel". */
  pooledOccupancyLabel?: string | null
  /** "Building ADR" at a combined inventory of two or more listings; null keeps "ADR". */
  adrLabel?: string | null
  adrData: Record<string, AdrDay>
  rateHistory: Record<string, RateHistoryEntry[]>
  targetMarkerByMonth?: Record<number, TargetMarker>
}

export default function Prices({
  currentUser,
  property,
  properties,
  month,
  year,
  roomTypes,
  ratePlans,
  selectedRatePlanId: selectedRatePlanIdProp,
  rates,
  allRoomTypeRates,
  daysInMonth,
  comparisonData,
  comparisonMode: comparisonModeParam,
  performance,
  occupancyData,
  roomOccupancy,
  totalRooms,
  pooledOccupancyLabel = null,
  adrLabel = null,
  adrData,
  rateHistory,
  targetMarkerByMonth = {},
  reportingScope,
  pageIdentity,
}: PricesProps) {
  const allRoomTypeIds = useMemo(() => roomTypes.map((rt) => rt.id), [roomTypes])

  const {
    view,
    selectedRoomTypeIds,
    selectedRatePlanId,
    comparisonMode: urlComparison,
    calendarRoomType,
    navigate,
  } = useCalendarNav({
    basePath: property.basePath,
    tab: 'prices',
    month,
    year,
    allRoomTypeIds,
    defaultRatePlanId: selectedRatePlanIdProp,
  })

  // Effective comparison — use URL param, fallback to server prop
  const comparisonMode = (urlComparison !== 'none' ? urlComparison : comparisonModeParam || 'none') as ComparisonMode

  // Calendar view uses a single room type. A room an older link names that is not one of the page's rows
  // (a Same room, before the calendar showed the building's rooms) falls back to the first row, as the
  // server's plan choice does.
  const calendarRoomTypeId =
    calendarRoomType != null && allRoomTypeIds.includes(calendarRoomType) ? calendarRoomType : roomTypes[0]?.id ?? 0
  const selectedCalendarRoom = useMemo(
    () => roomTypes.find((rt) => rt.id === calendarRoomTypeId),
    [roomTypes, calendarRoomTypeId]
  )
  // Backend-derived: the selected room isn't being priced — the grid area hosts
  // an explanation state instead of a wall of empty cells.
  const calendarNotPricedStatus =
    selectedCalendarRoom && isNotPriced(selectedCalendarRoom.pricingStatus)
      ? selectedCalendarRoom.pricingStatus
      : null

  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null)
  const [dayDetailRoomTypeId, setDayDetailRoomTypeId] = useState<number | null>(null)

  // Property-wide occupancy (for grid/timeline views)
  const propertyOccupancyByDate = useMemo(() => {
    const map: Record<string, OccupancyDay> = {}
    for (const d of occupancyData) map[d.date] = d
    return map
  }, [occupancyData])

  // Hover popup uses totalRooms as denominator to match the day-modal "Occupancy" insight.
  // Recompute pct from booked/totalRooms so the percentage matches the fraction (e.g. "7% 1/14", not "100% 1/14").
  const propertyOccupancyForPopup = useMemo(() => {
    const map: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }> = {}
    for (const d of occupancyData) {
      const pct = totalRooms > 0 ? Math.round((d.booked / totalRooms) * 100) : 0
      map[d.date] = { booked: d.booked, available: totalRooms, occupancyPct: pct, label: pooledOccupancyLabel }
    }
    return map
  }, [occupancyData, totalRooms, pooledOccupancyLabel])

  // Per-room-type occupancy for calendar view (single room type)
  const roomTypeOccupancyByDate = useMemo(() => {
    if (!roomOccupancy) return propertyOccupancyByDate
    const map: Record<string, OccupancyDay> = {}
    for (const day of daysInMonth) {
      const key = `${calendarRoomTypeId}:${day.date}`
      const ro = roomOccupancy[key]
      if (ro) {
        const pct = ro.occupancyPct ?? 0
        map[day.date] = { date: day.date, booked: ro.booked, available: ro.total, occupancyPct: pct, pickup7d: null, pickup30d: null, pickup60d: null, pickup7dRooms: null, pickup30dRooms: null, pickup60dRooms: null, paceRatio: null, baselineOccPct: null }
      }
    }
    return map
  }, [roomOccupancy, calendarRoomTypeId, daysInMonth, propertyOccupancyByDate])

  // THE clock for this page. Every date judgement below — past styling, the day
  // modal's edit gate, pace filters — reads this one value, resolved from the
  // PROPERTY's timezone rather than the viewer's. An operator in Cairo is two
  // hours into tomorrow while a London hotel is still selling tonight.
  const propertyToday = todayIso(property.timezone)

  // Use room-type-specific occupancy in calendar view, property-wide otherwise
  const occupancyByDate = view === 'calendar' ? roomTypeOccupancyByDate : propertyOccupancyByDate

  const avgPace = useMemo(() => {
    const futureWithPace = occupancyData
      .filter((d: OccupancyDay) => d.date > propertyToday && d.paceRatio != null)
    if (futureWithPace.length === 0) return null
    const sum = futureWithPace.reduce((acc: number, d: OccupancyDay) => acc + d.paceRatio!, 0)
    return parseFloat((sum / futureWithPace.length).toFixed(2))
  }, [occupancyData, propertyToday])

  const enrichedPerformance = useMemo(
    () => ({ ...performance, avgPace }),
    [performance, avgPace]
  )

  const filteredRoomTypes = useMemo(
    () => roomTypes.filter((rt) => selectedRoomTypeIds.includes(rt.id)),
    [roomTypes, selectedRoomTypeIds]
  )

  // When multiple room types are selected, use lead plan rates for all room types
  const multiRoomSelected = view !== 'calendar' && selectedRoomTypeIds.length > 1
  const activeRates = multiRoomSelected ? allRoomTypeRates : rates

  // The map the day modal must render = the map the CLICKED cell rendered. In
  // multi-room grid/timeline the cells render `allRoomTypeRates` (each room's
  // display plan), so the modal must too — no plan-scoped overlay. In calendar /
  // single-room the cells render the plan-scoped `rates`; that map only carries
  // the selected plan's OWN room, so merge it OVER the display map (selected room
  // matches its cell, every other room keeps its display-plan rate). Mirrors
  // `activeRates` so cell == modal on every view.
  const modalRates = useMemo(
    () => (multiRoomSelected ? allRoomTypeRates : { ...allRoomTypeRates, ...rates }),
    [multiRoomSelected, allRoomTypeRates, rates]
  )

  const rowMeta = useMemo(() => buildRoomTypeRowMeta(roomTypes), [roomTypes])

  // The building's rooms by the server's names: no hotel label on a row (owner ruling 2026-10-26).
  const roomTypeOptions = useMemo(
    () => roomTypes.map((rt) => ({
      value: rt.id.toString(),
      label: rt.name,
      notPriced: isNotPriced(rt.pricingStatus),
      ...rowMeta[rt.id],
    })),
    [roomTypes, rowMeta]
  )

  const multiRoomTypeOptions = useMemo(
    () => roomTypes.map((rt) => ({
      id: rt.id,
      name: rt.name,
      notPriced: isNotPriced(rt.pricingStatus),
      ...rowMeta[rt.id],
    })),
    [roomTypes, rowMeta]
  )

  // Active room type for rate plan filtering:
  // Calendar view → selected room type; Grid/Timeline → first selected (reference room is first after sort)
  const activeRoomTypeId = view === 'calendar'
    ? calendarRoomTypeId
    : selectedRoomTypeIds[0] ?? roomTypes[0]?.id ?? 0

  // Rate plans filtered to active room type only
  const activeRatePlans = useMemo(
    () => ratePlans.filter((rp) => rp.roomTypeId === activeRoomTypeId),
    [ratePlans, activeRoomTypeId]
  )

  const handleViewChange = useCallback((mode: ViewMode) => {
    navigate({ view: mode })
  }, [navigate])

  const handleMonthNavigate = useCallback((m: number, y: number) => {
    navigate({ month: m, year: y })
  }, [navigate])

  const handleComparisonChange = useCallback((mode: ComparisonMode) => {
    navigate({ comparison: mode })
  }, [navigate])

  // Find lead plan for a given room type
  const leadPlanIdFor = useCallback((rtId: number) => {
    const lead = ratePlans.find((rp) => rp.roomTypeId === rtId && rp.isLead)
    return lead?.id ?? ratePlans.find((rp) => rp.roomTypeId === rtId)?.id ?? null
  }, [ratePlans])

  const handleRoomTypeChange = useCallback((ids: number[]) => {
    // When multi-select changes, auto-select lead plan of the first selected room type
    const primaryId = ids[0]
    navigate({ roomTypes: ids, ratePlan: primaryId ? leadPlanIdFor(primaryId) : null })
  }, [navigate, leadPlanIdFor])

  const handleRatePlanChange = useCallback((value: string) => {
    navigate({ ratePlan: Number(value) })
  }, [navigate])

  const handleCalendarRoomTypeChange = useCallback((v: string) => {
    const rtId = parseInt(v, 10)
    // Auto-select the lead rate plan for the newly selected room type
    navigate({ calendarRoomType: rtId, ratePlan: leadPlanIdFor(rtId) })
  }, [navigate, leadPlanIdFor])

  const handleCellClick = useCallback((date: string, roomTypeId?: number) => {
    setDayDetailDate(date)
    setDayDetailRoomTypeId(roomTypeId ?? null)
  }, [])

  // Stable single-arg handler for the calendar view (binds the selected room
  // type) so the memoized CalendarMonthGrid doesn't re-render on every parent
  // render (e.g. opening/closing the day modal).
  const handleCalendarCellClick = useCallback(
    (date: string) => handleCellClick(date, calendarRoomTypeId),
    [handleCellClick, calendarRoomTypeId]
  )

  const basePath = `${property.basePath}/calendar`

  const figureScope = useMemo(
    () => ({ pooledLabel: pooledOccupancyLabel, adrLabel }),
    [pooledOccupancyLabel, adrLabel],
  )

  return (
    <OperatorLayout title="Prices" currentUser={currentUser} property={property} properties={properties}>
      {/* What the pooled figures cover, for the hover card, the day details and the month strip. */}
      <FigureScopeContext.Provider value={figureScope}>
      <div className="space-y-5">
        {/* Go Live banner for properties that haven't pushed rates yet */}
        <GoLiveBanner propertyBasePath={property.basePath} />

        {/* Page header with tabs and view switcher */}
        <div className="animate-slide-in flex items-center justify-between" style={{ animationDelay: '0ms' }}>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Rate Calendar</h1>
              <HelpTooltip
                content="View and manage rates across multiple layouts. Click any cell to create a quick override."
                learnMore="/guides/rate-calendar"
              />
            </div>
            {/* No identity line here (owner ruling 2026-10-26): the top bar already names the hotel and
                the business, and the Edit Prices review names both again at the moment of saving. */}
            <p className="mt-0.5 text-sm text-muted-foreground">Monthly rate overview</p>
          </div>

          <div className="flex items-center gap-3">
            <nav className="inline-flex h-9 items-center rounded-md bg-muted p-1 text-muted-foreground">
              <Link
                href={`${basePath}/prices?month=${month}&year=${year}${view !== 'calendar' ? `&view=${view}` : ''}`}
                className={cn(
                  'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm font-medium transition-all',
                  'bg-background text-foreground shadow-sm'
                )}
              >
                Prices
              </Link>
              <Link
                href={`${basePath}/occupancy?month=${month}&year=${year}${view !== 'calendar' ? `&view=${view}` : ''}`}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm font-medium transition-all text-muted-foreground hover:text-foreground"
              >
                Occupancy
              </Link>
            </nav>

            <ViewSwitcher value={view} onChange={handleViewChange} />
          </div>
        </div>

        <CalendarUpdateBanner />

        {/* Performance banner. A following listing's Prices calendar is this page at the lead (server
            302), so the page is always the building's or an ungrouped hotel's: nothing to caption. */}
        <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
          <PerformanceBanner metrics={enrichedPerformance} />
        </div>

        {/* Month picker + filters bar */}
        <div className="animate-slide-in" style={{ animationDelay: '80ms' }}>
          <MonthPicker month={month} year={year} onNavigate={handleMonthNavigate}>
            {view === 'calendar' ? (
              <SingleSelectDropdown
                label="Room Type"
                options={roomTypeOptions}
                value={calendarRoomTypeId.toString()}
                onChange={handleCalendarRoomTypeChange}
              />
            ) : (
              <MultiSelectDropdown
                label="Room Types"
                options={multiRoomTypeOptions}
                selectedIds={selectedRoomTypeIds}
                onChange={handleRoomTypeChange}
              />
            )}

            {!multiRoomSelected && (
              <Select
                value={selectedRatePlanId?.toString() ?? ''}
                onValueChange={handleRatePlanChange}
              >
                <SelectTrigger className="h-8 w-auto min-w-[180px] gap-1.5 rounded-lg border-border/60 bg-white px-3 py-1.5 text-xs font-medium shadow-none">
                  <span className="uppercase tracking-wider text-muted-foreground">Rate Plan</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activeRatePlans.map((rp) => (
                    <SelectItem key={rp.id} value={rp.id.toString()}>
                      <span className={rp.isLead ? 'font-semibold' : ''}>
                        {rp.name}
                      </span>
                      {rp.isLead && (
                        <span className="ml-1.5 inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                          Lead
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <ComparisonSelector value={comparisonMode} onChange={handleComparisonChange} />
          </MonthPicker>
        </div>

        {/* Legend (shown for grid and timeline) */}
        {(view === 'grid' || view === 'timeline') && (
          <div className="animate-slide-in flex flex-wrap items-center gap-3 text-xs" style={{ animationDelay: '100ms' }}>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-emerald-50 border border-emerald-200" />
              <span className="text-muted-foreground">Above base</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-rose-50 border border-rose-200" />
              <span className="text-muted-foreground">Below base</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border-2 border-blue-400 bg-white" />
              <span className="text-muted-foreground">Override</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-slate-200" />
              <span className="text-muted-foreground">Stop sell</span>
            </span>
          </div>
        )}

        {/* View content */}
        <div className="animate-slide-in" style={{ animationDelay: '120ms' }}>
          {view === 'calendar' && calendarNotPricedStatus && selectedCalendarRoom && (
            <NotPricedCalendarState
              status={calendarNotPricedStatus}
              roomName={selectedCalendarRoom.name}
              propertyBasePath={property.basePath}
            />
          )}

          {view === 'calendar' && !calendarNotPricedStatus && (
            <CalendarMonthGrid
              year={year}
              month={month}
              roomTypeId={calendarRoomTypeId}
              roomType={selectedCalendarRoom}
              rates={rates}
              occupancyByDate={occupancyByDate}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              comparisonData={comparisonData}
              comparisonMode={comparisonMode}
              currency={property.currency}
              onCellClick={handleCalendarCellClick}
              adrData={adrData}
              propertyBasePath={property.basePath}
              targetMarkerByMonth={targetMarkerByMonth}
              today={propertyToday}
            />
          )}

          {view === 'grid' && (
            <RateGrid
              today={propertyToday}
              daysInMonth={daysInMonth}
              roomTypes={filteredRoomTypes}
              rates={activeRates}
              currency={property.currency}
              onCellClick={handleCellClick}
              comparisonData={comparisonData}
              comparisonMode={comparisonMode}
              occupancyByDate={occupancyByDate}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              adrData={adrData}
              propertyBasePath={property.basePath}
            />
          )}

          {view === 'timeline' && (
            <TimelineGrid
              today={propertyToday}
              daysInMonth={daysInMonth}
              roomTypes={roomTypes}
              visibleRoomTypeIds={selectedRoomTypeIds}
              rates={activeRates}
              occupancyByDate={occupancyByDate}
              propertyOccupancyByDate={propertyOccupancyForPopup}
              comparisonData={comparisonData}
              comparisonMode={comparisonMode}
              currency={property.currency}
              onCellClick={handleCellClick}
              adrData={adrData}
              propertyBasePath={property.basePath}
            />
          )}
        </div>
      </div>

      <DayDetailModal
        open={dayDetailDate != null}
        onOpenChange={(open) => { if (!open) setDayDetailDate(null) }}
        selectedDate={dayDetailDate ?? ''}
        selectedRoomTypeId={dayDetailRoomTypeId}
        daysInMonth={daysInMonth}
        roomTypes={roomTypes}
        rates={modalRates}
        occupancyData={occupancyData}
        roomOccupancy={roomOccupancy}
        totalRooms={totalRooms}
        currency={property.currency}
        propertyBasePath={property.basePath}
        today={propertyToday}
        adrData={adrData}
        rateHistory={rateHistory}
        reportingScope={reportingScope}
        pageIdentity={pageIdentity}
      />
      </FigureScopeContext.Provider>
    </OperatorLayout>
  )
}
